import { useNavigate, useParams } from 'react-router-dom';
import { Sparkles, Download } from 'lucide-react';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Spinner } from '@/components/ui/spinner';
import { useTranslation } from '@/hooks/useTranslation';
import { logError } from '@/lib/logging/errorLogger';
import { useGaExperimentDetail } from '../hooks/useGaExperiments';
import { ExperimentComparisonCard } from '../components/ExperimentComparisonCard';
import { exportExperimentXlsx } from '../lib/experimentXlsx';
import { toolFieldLabelKey } from '../lib/toolFieldLabels';

export function ExperimentDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { experimentId } = useParams<{ experimentId: string }>();
  const { result, isLoading, error, retry } = useGaExperimentDetail(experimentId);
  const canExport = !!result && result.resultComparisons.length > 0;

  const breadcrumbItems = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    { label: t('toolRecommender.page.breadcrumb'), onClick: () => navigate('/tool-recommender') },
    { label: t('toolRecommender.detail.breadcrumb') },
  ];

  function handleExport() {
    if (!result) return;
    exportExperimentXlsx(
      result,
      {
        rank: t('toolRecommender.detail.exportRank'),
        material: t('toolRecommender.detail.exportMaterial'),
        operation: t('toolRecommender.detail.exportOperation'),
        baseToolArticle: t('toolRecommender.detail.exportBaseToolArticle'),
        baseScore: t('toolRecommender.detail.baseScoreLabel'),
        generatedScore: t('toolRecommender.detail.generatedScoreLabel'),
      },
      (key) => {
        const labelKey = toolFieldLabelKey(key);
        return labelKey ? t(labelKey) : key;
      }
    ).catch((err: unknown) => logError(err));
  }

  return (
    <PageLayout>
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          label={{ icon: Sparkles, text: t('toolRecommender.detail.breadcrumb') }}
          title={experimentId}
          mono
          actions={
            canExport && (
              <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
                <Download className="size-4" aria-hidden />
                {t('toolRecommender.detail.exportXlsx')}
              </Button>
            )
          }
        />

        {isLoading && (
          <div className="flex justify-center py-12 text-muted-foreground">
            <Spinner size="lg" label={t('common.loadingShort')} />
          </div>
        )}

        {error && !isLoading && (
          <Callout
            tone="error"
            action={
              <Button type="button" variant="link" size="inline" onClick={retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {t(error)}
          </Callout>
        )}

        {result &&
          !isLoading &&
          (result.resultComparisons.length === 0 ? (
            <EmptyState title={t('toolRecommender.detail.noResults')} />
          ) : (
            <ExperimentComparisonCard comparisons={result.resultComparisons} />
          ))}
      </div>
    </PageLayout>
  );
}
